import Cookies from "js-cookie";

import {
  AD_CHAT_LANDING_ID_COOKIE_KEY,
  AD_SOURCE_COOKIE_KEY,
  AD_SOURCE_FROM_ENTRY_COOKIE_KEY,
  extractAdSourceFromDeepLink,
  extractLandingIdFromDeepLink,
  extractQuerySourceFromDeepLink,
  isAdSkipSource,
  resolveEntryIntent,
} from "./adSource";
import { captureEntryIntent } from "./entryIntent.client";
import {
  getAdChatLandingIdFromStore,
  setAdChatLandingIdInStore,
  setAdSourceInStore,
  setLandingIdInStore,
  setQuerySourceInStore,
} from "./adSourceStore";

const COOKIE_OPTIONS: Cookies.CookieAttributes = {
  expires: 365,
  path: "/",
  sameSite: "Lax",
};

function withSecureFlag(): Cookies.CookieAttributes {
  if (typeof window === "undefined") {
    return COOKIE_OPTIONS;
  }
  return {
    ...COOKIE_OPTIONS,
    secure: window.location.protocol === "https:",
  };
}

/** 读取本地 ad_source cookie；无则 null。 */
export function getAdSourceCookie(): string | null {
  if (typeof document === "undefined") {
    return null;
  }
  const raw = Cookies.get(AD_SOURCE_COOKIE_KEY)?.trim();
  return raw ? raw : null;
}

/** 本次入口（URL 参数 / 原生深链）是否带了 ad_source；命中则后端回填不覆盖本地。 */
export function isAdSourceFromEntry(): boolean {
  if (typeof document === "undefined") {
    return false;
  }
  return Cookies.get(AD_SOURCE_FROM_ENTRY_COOKIE_KEY) === "1";
}

/** 深链不带 ad_source 不算「本次点了广告」，清掉标记让后端照常回填。 */
function markAdSourceFromEntry(fromEntry: boolean): void {
  if (typeof document === "undefined") {
    return;
  }
  if (fromEntry) {
    // 不设 expires = 会话 cookie
    Cookies.set(AD_SOURCE_FROM_ENTRY_COOKIE_KEY, "1", {
      path: "/",
      sameSite: "Lax",
      secure: window.location.protocol === "https:",
    });
    return;
  }
  Cookies.remove(AD_SOURCE_FROM_ENTRY_COOKIE_KEY, { path: "/" });
}

/**
 * 写入 ad_source cookie。
 * 调用方负责覆盖语义：后端 ownerInfo.ad_source 优先覆盖本地（见 useOnboardingEntryGate / fetchUserInfo）。
 */
export function setAdSourceCookie(value: string): void {
  if (typeof document === "undefined") {
    return;
  }
  const trimmed = value?.trim();
  if (!trimmed) {
    return;
  }
  Cookies.set(AD_SOURCE_COOKIE_KEY, trimmed.toUpperCase(), withSecureFlag());
}

/**
 * Chat 广告承接的 landing_id：专用 store 优先（原生深链写入），其次读 Proxy 落的一次性 cookie
 * （web 浏览器落地，见 `AD_CHAT_LANDING_ID_COOKIE_KEY`）并转存专用 store 后删除 cookie。
 * 转存后生命周期与深链一致：会话内有效、刷新即回默认。**不写通用 landingId** —— 那个会进
 * Brief feed 并被计消，共用会让重进 Ask Ed 丢广告问题、承接埋点静默不发。
 */
export function resolveAdChatLandingId(): string | null {
  const fromStore = getAdChatLandingIdFromStore();
  if (fromStore || typeof document === "undefined") {
    return fromStore;
  }
  const fromCookie = Cookies.get(AD_CHAT_LANDING_ID_COOKIE_KEY)?.trim();
  if (!fromCookie) {
    return null;
  }
  setAdChatLandingIdInStore(fromCookie);
  Cookies.remove(AD_CHAT_LANDING_ID_COOKIE_KEY, { path: "/" });
  return fromCookie;
}

/** 当前 cookie 的 ad_source 是否应跳过 onboarding 直落 Brief。 */
export function shouldAdSkipOnboarding(): boolean {
  return isAdSkipSource(getAdSourceCookie());
}

/** 从深链 payload 提取 ad_source + query_source + landing_id 并写入（ad_source→cookie+store，query_source/landing_id→仅 store）。 */
export function captureAdSourceFromDeepLink(
  data: Parameters<typeof extractAdSourceFromDeepLink>[0],
): string | null {
  const adSource = extractAdSourceFromDeepLink(data);
  if (adSource) {
    setAdSourceCookie(adSource);
    setAdSourceInStore(adSource);
  }
  markAdSourceFromEntry(!!adSource);
  // query_source：只存 store，不存 cookie
  const querySource = extractQuerySourceFromDeepLink(data);
  if (querySource) {
    setQuerySourceInStore(querySource);
  }
  // landing_id：只存内存 store（会话级），不写 cookie，不影响 ad_source 跳过 onboarding 判断
  const landingId = extractLandingIdFromDeepLink(data);
  if (landingId) {
    setLandingIdInStore(landingId);
  }
  // 落点意图 + Chat 承接专用 landing_id：只由本次深链写入（后端回填不走这里）
  if (resolveEntryIntent(adSource) === "chat") {
    setAdChatLandingIdInStore(landingId);
  }
  captureEntryIntent(adSource);
  return adSource;
}
